import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Alert,
  Divider,
  Chip,
  IconButton,
  Skeleton,
  FormLabel
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useDispatch, useSelector } from "react-redux";
import { actionRowDataSelector } from "../../services/redux/slices/disbursementButtonSlice";
import { getDateRange } from "../../utils/dateFormatUtils";
// Styled components
const StyledContainer = styled(Box)(({ theme }) => ({
  maxWidth: "96%",
  margin: "0 auto",
  padding: "0",
  backgroundColor: "transparent",
  marginTop: "24px",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    fontSize: "14px",
    color: "#333",
    height: "40px",
    textAlign: "left !important",
    "& fieldset": {
      borderColor: "#ddd",
    },
    "&:hover fieldset": {
      borderColor: "#ddd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#ddd",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f8f9fa",
      "& fieldset": {
        borderColor: "#ddd",
      },
    },
  },
  "& .MuiInputBase-input": {
    padding: "10px 12px",
    fontSize: "14px",
    color: "#666",
    "&.Mui-disabled": {
      WebkitTextFillColor: "#666",
    },
  },
  "& input::placeholder": {
    textAlign: "left !important", // ✅ placeholder align
    color: "#999", // optional styling for placeholder
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: "600",
  color: "#00b3b5",
  textTransform: "uppercase",
  marginBottom: "16px",
  letterSpacing: "0.5px",
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: "400",
  color: "#333",
  marginBottom: "0px",
}));

const BackButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  color: "#00b3b5",
  border: "2px solid #00b3b5",
  borderRadius: "6px",
  padding: "8px 16px",
  fontSize: "14px",
  fontWeight: "600",
  "&:hover": {
    backgroundColor: "rgba(0, 179, 181, 0.1)",
    border: "2px solid #00b3b5",
  },
}));

const SkipButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  color: "#666",
  border: "1px solid #ddd",
  borderRadius: "6px",
  padding: "10px 20px",
  fontSize: "14px",
  fontWeight: "500",
  "&:hover": {
    backgroundColor: "#f5f5f5",
    border: "1px solid #ccc",
  },
}));

const ProvideConsentButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  color: "#fff",
  backgroundColor: "#00b3b5",
  border: "2px solid #00b3b5",
  borderRadius: "6px",
  padding: "10px 20px",
  fontSize: "14px",
  fontWeight: "600",
  "&:hover": {
    backgroundColor: "#008a8c",
    border: "2px solid #008a8c",
  },
}));

const StatusBox = styled(Box)(({ theme }) => ({
  display: "inline-block",
  padding: "6px 12px",
  backgroundColor: "#f8f9fa",
  border: "1px solid #e0e0e0",
  borderRadius: "4px",
  fontSize: "14px",
  color: "#666",
}));
interface AggregatorData {
  status: string;
  // add more fields if needed
}
interface AccountAggregatorProps {
  onBack?: () => void;
  onSkipConsent?: () => void;
  onProvideConsent?: () => void;
  setMobileNumbers?:React.Dispatch<React.SetStateAction<string>>;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
  mobileNumber?: string;
  consentStatus?: string;
  dateRange?: string;
  bankListLien?: any[];
  aggregatorData?: AggregatorData | null;
  validConsent?: any;
  isLoadingUpdateNew:boolean;
  continueButton:boolean;
  isEditable:boolean;
}
const AccountAggregator: React.FC<AccountAggregatorProps> = ({
  onBack,
  onSkipConsent,
  onProvideConsent,
  setMobileNumbers,
  bankName,
  accountNumber,
  ifscCode,
  mobileNumber,
  consentStatus,
  dateRange,
  bankListLien = [],
  aggregatorData = null,
  validConsent=false,
  isLoadingUpdateNew=true,
  continueButton=false,
  isEditable=false,
}) => {
  console.log(continueButton,"continueButton",validConsent)
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [isMobileEditable, setIsMobileEditable] = useState<boolean>(false);
  const actionRowData = useSelector(actionRowDataSelector);
  const bankNameVal =
    (bankListLien &&
      bankListLien?.find((bank) => bank.id === bankName)?.bankName) ||
    "";
  return (
    <>
      <Divider sx={{ paddingTop: "20px" }} />
      <StyledContainer>
        {/* Note Section */}
        <Typography
          variant="body2"
          sx={{ fontWeight: "600", mb: 1, fontSize: "14px", color: "#00b3b5" }}
        >
          NOTE
        </Typography>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "24px",
          }}
        >
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.5, fontSize: "14px", color: "#666" }}
          >
            Your consent allows us to securely fetch the balance of your lien
            account automatically, expediting your fund release request. After
            verifying the linked mobile no. below, please click{" "}
            <Box component="span" sx={{ color: "#00b3b5", fontWeight: "600" }}>
              "Provide consent"
            </Box>{" "}
            and ensure you select the same lien-marked account you mentioned in
            your fund release request and approve consent.
          </Typography>
        </Box>

        {/* Lien A/C Details Section */}
        <SectionTitle>LIEN A/C DETAILS FOR CONSENT</SectionTitle>
        {/* Form Fields */}
        {/* <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              value={bankNameVal}
              fullWidth
              size="small"
              InputProps={{ readOnly: true ,
                sx: {
                  fontSize: "14px", // ✅ input text size
                },
              }}
              label="Bank Name"
              disabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              value={accountNumber}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true,
                sx: {
                  fontSize: "14px", // ✅ input text size
                }, }}
              label="Account No"
              disabled={true}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              value={ifscCode}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true,
                sx: {
                  fontSize: "14px", // ✅ input text size
                }, }}
              label="IFSC Code"
              disabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6} display="flex" alignItems="center">
            <TextField
              value={mobileNumber}
              fullWidth
              size="small"
              label="Mobile No. linked to this Bank account"
              disabled={!isMobileEditable}
              variant="outlined"   // ✅ outlined instead of filled
              InputLabelProps={{ shrink: true }}  // ✅ prevents overlap
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ""); // remove non-digits
                setMobileNumbers?.(value);
              }}
              InputProps={{
                readOnly: !isMobileEditable,
                sx: {
                  fontSize: "14px",   // ✅ input text size
                  pr: 1               // ✅ cleaner paddingRight instead of style object
                },
                endAdornment:
                  ((validConsent === false || validConsent === "in-progress") &&  continueButton === false && isEditable === true) ? (
                    <IconButton onClick={() => setIsMobileEditable(!isMobileEditable)}>
                      <EditIcon sx={{ fontSize: 18, color: "#00a5a8" }} />
                    </IconButton>
                  ) : null,
              }}
              inputProps={{ maxLength: 10 }}
            />

          </Grid>
        </Grid> */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormLabel>Bank Name</FormLabel>
            <TextField
              value={bankNameVal}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: { fontSize: "14px" },
              }}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormLabel>Account No</FormLabel>
            <TextField
              value={accountNumber}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: { fontSize: "14px" },
              }}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormLabel>IFSC Code</FormLabel>
            <TextField
              value={ifscCode}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: { fontSize: "14px" },
              }}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={6} display="flex" flexDirection="column">
            <FormLabel>Mobile No. linked to this Bank account</FormLabel>
            <TextField
              value={mobileNumber}
              fullWidth
              size="small"
              disabled={!isMobileEditable}
              variant="outlined"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ""); // remove non-digits
                setMobileNumbers?.(value);
              }}
              InputProps={{
                readOnly: !isMobileEditable,
                sx: { fontSize: "14px", pr: 1 },
                endAdornment:
                  (validConsent === false ||
                  validConsent === "in-progress")
                    && continueButton === false && isEditable === true ? (
                        <IconButton
                          onClick={() => setIsMobileEditable(!isMobileEditable)}
                        >
                          <EditIcon sx={{ fontSize: 18, color: "#00a5a8" }} />
                        </IconButton>
                      )
                    : null,
              }}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
        </Grid>
        {/* Verification Note */}
        <Box sx={{ mt: 2 ,mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ color: "#666", lineHeight: 1.4, fontSize: "14px" }}
          >
            Please verify the above mobile no. linked to your lien account is
            accurate. An OTP will be sent to this number when you proceed for
            consent.
          </Typography>
        </Box>
        {/* Consent Status */}
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, fontSize: "13px", mb: 1 }}
          >
            Consent Status
          </Typography>
          {isLoadingUpdateNew? 
          <Skeleton
            variant="rectangular"
            width={120}
            height={22}
            sx={{ borderRadius: "4px", marginBottom: "5px" }}
          />:
          (<>
            { validConsent===true &&
              <Chip
                label="Consent received"
                sx={{
                  bgcolor: "#068906",
                  color: "white",
                  fontWeight: 500,
                  fontSize: "12px",
                  borderRadius: "4px !important",
                  height: "22px",
                  marginBottom:"5px"
                }}
              />
            }
            {validConsent===false &&
              <Chip
                label="No consent received"
                sx={{
                  bgcolor: "#ff8f24",
                  color: "white",
                  fontWeight: 500,
                  fontSize: "12px",
                  borderRadius: "4px !important",
                  height: "22px",
                  marginBottom:"5px"
                }}
              />
            }
            {validConsent==="in-progress" &&
              <Chip
                label="In Progress"
                sx={{
                  bgcolor: "#ff8f24",
                  color: "white",
                  fontWeight: 500,
                  fontSize: "12px",
                  borderRadius: "4px !important",
                  height: "22px",
                  marginBottom:"5px"
                }}
              />
            }
          </>)}
        </Box>

        {/* Date Range */}
         <Typography variant="body2" sx={{ color: "#999", fontSize: "12px" }}>
            {getDateRange(actionRowData?.loanDetails?.loanEndDate,actionRowData?.loanDetails?.loanStartDate)}
          </Typography>
      </StyledContainer>
    </>
  );
};

export default AccountAggregator;
